import layoutEngine from "../../libraries/layoutEngine";
import ParametricDotsLibrary from "../../libraries/ParametricDotsLibrary";
import {
  getPlacedPCell,
  updatePlacedPCellParameters,
} from "../store/placedPCellStore";
import { rebuildPCellFromDefinition } from "./pcellInterpreter";

const SOURCE_TYPE_MAP = {
  I: "int",
  F: "float",
  B: "binary",
  S: "string",
  LPP: "layer",
};

const getTargetParameterName = (parameter = {}) =>
  parameter.originalName || parameter.name || "";

const getSourceValue = (source) =>
  source?.defaultValue ?? source?.defaultVal ?? source?.value;

const isCompatibleType = (targetType, sourceType) =>
  targetType === sourceType ||
  (targetType === "int" && sourceType === "float") ||
  (targetType === "float" && sourceType === "int");

const isValidSourceValue = (source) => {
  const sourceType = source?.type || source?.datatype;
  const rawValue = getSourceValue(source);

  if (sourceType === "LPP") {
    return Boolean(
      rawValue &&
        typeof rawValue === "object" &&
        String(rawValue.layer || "").trim() &&
        String(rawValue.datatype || "").trim()
    );
  }

  const value = String(rawValue ?? "").trim();

  if (!value) return false;
  if (sourceType === "I") return /^-?\d+$/.test(value);
  if (sourceType === "F") return /^-?(?:\d+\.\d+|\.\d+)$/.test(value);
  if (sourceType === "B") return /^(0|1|T|nil)$/i.test(value);
  if (sourceType === "S") return /^[A-Za-z0-9_]+$/.test(value);

  return false;
};

const convertSourceValue = (targetType, source) => {
  const rawValue = getSourceValue(source);

  if (targetType === "layer") {
    return {
      layer: rawValue.layer,
      datatype: rawValue.datatype,
    };
  }

  if (targetType === "int" || targetType === "float") {
    return parseFloat(rawValue);
  }

  if (targetType === "binary") {
    return rawValue === true ||
      rawValue === 1 ||
      rawValue === "1" ||
      String(rawValue).toUpperCase() === "T"
      ? 1
      : 0;
  }

  return String(rawValue);
};

const parseMappingTarget = (mappingKey) => {
  const separatorIndex = mappingKey.lastIndexOf(":");

  if (separatorIndex <= 0 || separatorIndex === mappingKey.length - 1) {
    return null;
  }

  return {
    cellName: mappingKey.slice(0, separatorIndex),
    parameterName: mappingKey.slice(separatorIndex + 1),
  };
};

const refreshChangedPCells = (changedCellNames) => {
  if (typeof window === "undefined" || !changedCellNames.size) return;

  const engine = window.canvasEngine;
  if (!engine) return;

  const parentCells = new Map();

  for (const cell of [
    ...(layoutEngine.getCells?.() || []),
    ...(layoutEngine.getUploadedCells?.() || []),
  ]) {
    if (cell?.name && !parentCells.has(cell.name)) {
      parentCells.set(cell.name, cell);
    }
  }

  for (const [parentCellName, parentCell] of parentCells) {
    const changedInstances = (parentCell.instances || []).filter(
      (instance) => changedCellNames.has(instance.ref) && instance.id
    );

    if (!changedInstances.length) continue;

    ParametricDotsLibrary.redraw(engine, {
      parentCellName,
      gridSize: engine.dotGridSize || ParametricDotsLibrary.gridSize || 3,
      refresh: true,
    });

    for (const instance of changedInstances) {
      ParametricDotsLibrary.maintainConnectionsForChangedInstance(
        engine,
        parentCellName,
        instance.id
      );
    }

    ParametricDotsLibrary.redraw(engine, {
      parentCellName,
      gridSize: engine.dotGridSize || ParametricDotsLibrary.gridSize || 3,
      refresh: true,
    });
  }

  ParametricDotsLibrary.applyToEngine(engine);
  window.dispatchEvent(new Event("hierarchy-updated"));
  engine.draw?.();
};

export const propagateMappedParameterChanges = ({
  sourceScope,
  changedSourceIds,
  globalParameters,
  localParameters,
  parameterMappings,
}) => {
  const changedIds = new Set(changedSourceIds || []);
  if (!changedIds.size) return [];

  const sourceRows =
    sourceScope === "global" ? globalParameters : localParameters;
  const sourceById = new Map(sourceRows.map((row) => [row.id, row]));
  const changedPCellParameters = new Map();

  for (const [mappingKey, mapping] of Object.entries(
    parameterMappings || {}
  )) {
    if (
      mapping?.sourceScope !== sourceScope ||
      !changedIds.has(mapping.sourceId)
    ) {
      continue;
    }

    const target = parseMappingTarget(mappingKey);
    const source = sourceById.get(mapping.sourceId);

    if (!target || !source || !isValidSourceValue(source)) continue;

    const placed = getPlacedPCell(target.cellName);
    if (!placed) continue;

    const currentParameters =
      changedPCellParameters.get(target.cellName) || placed.parameters || [];
    const targetParameter = currentParameters.find(
      (parameter) =>
        getTargetParameterName(parameter) === target.parameterName
    );

    if (!targetParameter) continue;

    const sourceType = SOURCE_TYPE_MAP[source.type] || source.type;
    if (!isCompatibleType(targetParameter.type, sourceType)) continue;

    const nextValue = convertSourceValue(targetParameter.type, source);
    const nextParameters = currentParameters.map((parameter) =>
      getTargetParameterName(parameter) === target.parameterName
        ? { ...parameter, value: nextValue }
        : { ...parameter }
    );

    changedPCellParameters.set(target.cellName, nextParameters);
  }

  const changedCellNames = new Set();

  for (const [cellName, nextParameters] of changedPCellParameters) {
    const placed = getPlacedPCell(cellName);
    if (!placed?.definitionSnapshot) continue;

    updatePlacedPCellParameters(cellName, nextParameters);
    rebuildPCellFromDefinition(
      cellName,
      placed.definitionSnapshot,
      nextParameters
    );
    changedCellNames.add(cellName);
  }

  refreshChangedPCells(changedCellNames);
  return Array.from(changedCellNames);
};

export const applyPCellParameterUpdates = (updates = []) => {
  const updatesByCell = new Map();

  for (const update of updates) {
    if (!update?.cellName || !update?.parameterName) continue;

    if (!updatesByCell.has(update.cellName)) {
      updatesByCell.set(update.cellName, new Map());
    }

    updatesByCell
      .get(update.cellName)
      .set(update.parameterName, update.value);
  }

  const changedCellNames = new Set();

  for (const [cellName, parameterUpdates] of updatesByCell) {
    const placed = getPlacedPCell(cellName);
    if (!placed?.definitionSnapshot) {
      throw new Error(`PCell "${cellName}" is no longer available.`);
    }

    const existingNames = new Set(
      (placed.parameters || []).map(getTargetParameterName)
    );

    for (const parameterName of parameterUpdates.keys()) {
      if (!existingNames.has(parameterName)) {
        throw new Error(
          `Parameter "${parameterName}" is no longer available on ${cellName}.`
        );
      }
    }

    const nextParameters = (placed.parameters || []).map((parameter) => {
      const parameterName = getTargetParameterName(parameter);
      return parameterUpdates.has(parameterName)
        ? { ...parameter, value: parameterUpdates.get(parameterName) }
        : { ...parameter };
    });

    updatePlacedPCellParameters(cellName, nextParameters);
    rebuildPCellFromDefinition(
      cellName,
      placed.definitionSnapshot,
      nextParameters
    );
    changedCellNames.add(cellName);
  }

  refreshChangedPCells(changedCellNames);
  return Array.from(changedCellNames);
};
